import { Auth, CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";

import React, { useEffect, useState } from "react";

import EmailIcon from "@mui/icons-material/Email";
import KeyIcon from "@mui/icons-material/Key";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Checkbox, Paper, TextField, Typography } from "@mui/material";

import useCheckUserStatus from "@/hooks/useCheckUserStatus";
import useNavigateLogin from "@/hooks/useNavigateLogin";
import useSnackbar from "@/hooks/useSnackbar";
import useUserInfo from "@/hooks/useUserInfo";

import googleLogo from "../images/google_log.png";
import useSignInStore from "../useSignInStore";

export interface AuthCardProps {
  title: string;
  googleButtonText: string;
  passwordButtonText: string;
  magicCodeButtonText: string;
  redirectText: string;
  redirectButtonText: string;
  onRedirectClick: () => void;
  onOptVerify: (v: string) => void;
  isSignIn: boolean;
  requireTermsCheck?: boolean;
  onPasswordButtonClick?: () => void;
}

function AuthCard(props: AuthCardProps) {
  const {
    title,
    googleButtonText,
    passwordButtonText,
    magicCodeButtonText,
    redirectText,
    redirectButtonText,
    onRedirectClick,
    onOptVerify,
    isSignIn,
    requireTermsCheck = false,
    onPasswordButtonClick,
  } = props;

  const [number, setNumber] = useState("");
  const password = Math.random().toString(10) + "Abc#";

  const [loading, setLoading] = useState(false);

  const { getRedirectUrl } = useNavigateLogin();
  const { enqueueSnackbar } = useSnackbar();
  const { userInfoContext } = useUserInfo();
  const { checkUserStatus } = useCheckUserStatus();
  const setSignInModalVisible = useSignInStore((state) => state.setSignInModalVisible);

  const [checked, setChecked] = useState(false);
  const [checkedMsg, setCheckedMsg] = useState(false);

  async function sendMagicLink() {
    if (!number.trim()) {
      enqueueSnackbar("Email cannot be empty.");
      return;
    }
    setLoading(true);

    Auth.signIn(number)
      .then((result) => {
        console.log("USER FOUND IN", result);
      })
      .catch((e) => {
        if (e.code === "UserNotFoundException") {
          signUp();
        } else {
          console.log(e.code);
          console.error(e);
        }
      });

    const response = await fetch("https://api.awardtool.com/auth/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: number,
        url: window.location.origin,
        origin_path: getRedirectUrl(),
      }),
    }).catch((err) => {
      alert(`Failed to send magic link: ${err.message}`);
    });

    setLoading(false);

    if (response?.status !== 202) {
      const responseBody = await response?.json();
      console.log(`Failed to send magic link: ${responseBody.message}`);
    } else {
      onOptVerify(number);
    }
  }

  async function signIn(email = "") {
    try {
      const cUser = await Auth.signIn(email);
      if (cUser) {
        userInfoContext.setUserInfo({
          email: cUser.attributes.email,
          token: cUser.signInUserSession.accessToken.jwtToken,
          profile_img: cUser.attributes?.picture,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  const signUp = async () => {
    const result = await Auth.signUp({
      username: number,
      password,
      attributes: {
        email: number,
      },
    }).then(() => signIn(number));
    return result;
  };

  useEffect(() => {
    checkUserStatus();
  }, [checkUserStatus]);

  const handleMagicCodeClick = () => {
    if (requireTermsCheck && !checked) {
      setCheckedMsg(true);
    } else {
      sendMagicLink();
    }
  };

  const handleGoogleSignIn = () => {
    if (requireTermsCheck && !checked) {
      setCheckedMsg(true);
      return;
    }
    Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google });
  };

  return (
    <Paper elevation={4} className="!rounded-lg max-w-sm m-auto">
      <div className="py-[32px] px-[24px]">
        <Typography className="!mb-[16px] text-center !text-2xl !font-bold">{title}</Typography>

        <Button
          fullWidth
          size="large"
          variant="outlined"
          startIcon={<img src={googleLogo} className="w-[18px]" />}
          onClick={handleGoogleSignIn}
          className="!mb-[10px]"
        >
          {googleButtonText}
        </Button>

        <Button fullWidth size="large" variant="contained" onClick={onPasswordButtonClick}>
          <KeyIcon className="!text-[20px] mr-1" />
          {passwordButtonText}
        </Button>

        <div className="flex my-[8px] items-center justify-center px-[10px]">
          <Box
            className="flex flex-1 h-[1px]"
            sx={{ backgroundColor: (theme) => theme.palette.divider }}
          />
          <Typography variant="body2" className="px-[12px] !text-neutral-400">
            or
          </Typography>
          <Box
            className="flex flex-1 h-[1px]"
            sx={{ backgroundColor: (theme) => theme.palette.divider }}
          />
        </div>

        <Typography className="!mb-[16px] text-center !text-[14px] !font-semihold">
          A magic code will be sent to your email for easy &amp; secure{" "}
          {isSignIn ? "log in" : "sign up"}.
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Email"
          value={number}
          inputProps={{ autoComplete: "off" }}
          InputProps={{ style: { height: "46px" } }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setNumber(event.target.value.toLowerCase());
          }}
        />

        <LoadingButton
          loading={loading}
          fullWidth
          size="large"
          color="primary"
          variant="contained"
          className="!mt-[16px]"
          startIcon={<EmailIcon className="!text-[20px]" />}
          onClick={handleMagicCodeClick}
        >
          {magicCodeButtonText}
        </LoadingButton>

        {checkedMsg && requireTermsCheck ? (
          <div className="mt-[25px]">
            <Typography
              variant="body2"
              className="transition-all animate-bounce text-center"
              sx={{ color: (theme) => theme.palette.primary.main }}
            >
              You must agree to the terms and conditions first
            </Typography>
          </div>
        ) : null}

        <div className="flex mt-[16px] items-center justify-center">
          <Typography variant="body2">{redirectText}</Typography>
          <Button
            variant="text"
            onClick={onRedirectClick}
            color="primary"
            sx={{
              "&:hover": { backgroundColor: "transparent" },
              minWidth: 0,
            }}
          >
            {redirectButtonText}
          </Button>
        </div>

        <Typography variant="body2" textAlign="center" className="pt-[16px]">
          {requireTermsCheck && (
            <Checkbox
              checked={checked}
              onChange={(e) => {
                setChecked(e.target.checked);
                if (e.target.checked) {
                  setCheckedMsg(false);
                }
              }}
              sx={{ padding: 0, paddingRight: "8px" }}
              className="translate-y-[-1px]"
            />
          )}
          By {isSignIn ? "logging in" : "signing up"}, you agree to the{" "}
          <Typography
            variant="body2"
            component="a"
            href="/"
            className="no-underline text-inherit"
            sx={{ color: (theme) => theme.palette.primary.main, display: "inline" }}
          >
            Terms of Service
          </Typography>
          &amp;{" "}
          <Typography
            variant="body2"
            component="a"
            href="/privacy-policy"
            className="no-underline text-inherit"
            sx={{ color: (theme) => theme.palette.primary.main, display: "inline" }}
          >
            Privacy Policy
          </Typography>
        </Typography>
      </div>
    </Paper>
  );
}

export default AuthCard;
